
import { useState, useMemo } from "react";
import RecipeFilters, { SortOption, FilterOption, GroupOption } from "./recipe-collection/RecipeFilters";
import MobileRecipeFilters from "./recipe-collection/MobileRecipeFilters";
import { useUnifiedContent } from "@/hooks/useUnifiedContent";
import { UnifiedContent } from "@/types/content";
import EmptyRecipeState from "./recipe-collection/EmptyRecipeState";
import NoResultsMessage from "./recipe-collection/NoResultsMessage";
import RecipeLoadingState from "./recipe-collection/RecipeLoadingState";
import UnifiedContentGrid from "./recipe-collection/UnifiedContentGrid";
import { useIsMobile } from "@/hooks/use-mobile";

interface RecipeCollectionProps {
  onRecipeDeleted?: () => void;
  onCompositionEdit?: (composition: any) => void;
  onCompositionDelete?: (composition: any) => void;
  selectedTags?: string[];
  onTagSelect?: (tag: string) => void;
  onTagRemove?: (tag: string) => void;
  onClearAllTags?: () => void;
}

const RecipeCollection = ({ 
  onCompositionEdit, 
  onCompositionDelete,
  selectedTags = [],
  onTagSelect,
  onTagRemove,
  onClearAllTags,
}: RecipeCollectionProps) => {
  const isMobile = useIsMobile();
  
  // Filter and sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("date-desc");
  const [filterBy, setFilterBy] = useState<FilterOption>("all");
  const [groupBy, setGroupBy] = useState<GroupOption>("all");
  const [excludeCompositions, setExcludeCompositions] = useState(false);

  const { content, recipes, isLoading, refetch } = useUnifiedContent();

  const handleClearFilters = () => {
    setSearchQuery("");
    setSortBy("date-desc");
    setFilterBy("all");
    setGroupBy("all");
    setExcludeCompositions(false);
    onClearAllTags?.();
  };

  // Filter and sort content
  const filteredAndSortedContent = useMemo(() => {
    let filtered: UnifiedContent[] = [...content];

    // Exclude compositions filter - apply this first
    if (excludeCompositions) {
      filtered = filtered.filter(item => item.type === "recipe");
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.type === 'composition' && item.composition?.recipes.some(recipe =>
          recipe.title.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }

    // Tag filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(item => {
        if (item.type === 'recipe' && item.recipe?.tags) {
          return selectedTags.every(selectedTag => 
            item.recipe!.tags!.includes(selectedTag)
          );
        } else if (item.type === 'composition' && item.composition?.tags) {
          return selectedTags.every(selectedTag =>
            item.composition!.tags!.includes(selectedTag)
          );
        }
        return false;
      });
    }

    // Group filter - only apply to recipes, always include compositions unless excluded above
    if (groupBy !== "all") {
      filtered = filtered.filter(item => {
        // Always include compositions (they were already filtered above if needed)
        if (item.type === "composition") {
          return true;
        }
        
        // Apply group filter only to recipes
        if (item.type !== "recipe" || !item.recipe) {
          return false;
        }
        
        const recipe = item.recipe as any; // Type assertion for stats
        const now = new Date();
        const createdDate = new Date(item.createdAt);
        const daysSinceCreated = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        
        switch (groupBy) {
          case "never-used":
            return recipe.usageCount === 0;
          case "recently-used":
            // Include both recently used recipes AND recently created recipes (within last 7 days)
            const isRecentlyUsed = recipe.usageCount > 0 && (recipe.daysSinceLastUse === null || recipe.daysSinceLastUse <= 15);
            const isRecentlyCreated = daysSinceCreated <= 7;
            return isRecentlyUsed || isRecentlyCreated;
          default:
            return true;
        }
      });
    }

    // Sort
    switch (sortBy) {
      case "alphabetical-asc":
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case "alphabetical-desc":
        filtered.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case "date-asc":
        filtered.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case "date-desc":
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case "usage-desc":
        filtered.sort((a, b) => {
          if (a.type !== "recipe" || b.type !== "recipe") return 0;
          const aUsage = (a.recipe as any)?.usageCount || 0;
          const bUsage = (b.recipe as any)?.usageCount || 0;
          return bUsage - aUsage;
        });
        break;
      case "usage-asc":
        filtered.sort((a, b) => {
          if (a.type !== "recipe" || b.type !== "recipe") return 0;
          const aUsage = (a.recipe as any)?.usageCount || 0;
          const bUsage = (b.recipe as any)?.usageCount || 0;
          return aUsage - bUsage;
        });
        break;
    }

    return filtered;
  }, [content, searchQuery, sortBy, groupBy, excludeCompositions, selectedTags]);

  if (isLoading) {
    return <RecipeLoadingState />;
  }

  if (recipes.length === 0) {
    return <EmptyRecipeState />;
  }

  return (
    <>
      {/* Filters */}
      {isMobile ? (
        <MobileRecipeFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          selectedTags={selectedTags}
          onTagRemove={onTagRemove}
        />
      ) : (
        <RecipeFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          filterBy={filterBy}
          onFilterChange={setFilterBy}
          groupBy={groupBy}
          onGroupChange={setGroupBy}
          excludeCompositions={excludeCompositions}
          onExcludeCompositionsChange={setExcludeCompositions}
          selectedTags={selectedTags}
          onTagRemove={onTagRemove}
          onClearFilters={handleClearFilters}
        />
      )}

      {/* Results */}
      {filteredAndSortedContent.length === 0 ? (
        <NoResultsMessage onClearFilters={handleClearFilters} />
      ) : (
        <UnifiedContentGrid 
          content={filteredAndSortedContent}
          onCompositionEdit={onCompositionEdit}
          onCompositionDelete={onCompositionDelete}
          onTagSelect={onTagSelect}
          isMobile={isMobile}
        />
      )}
    </>
  );
};

export default RecipeCollection;
